import classNames from 'classnames';

import Button from '../../../components/Button';
import Card from '../../../components/Card';
import Pagination from '../../../components/Pagination';
import { EbookStatus, PintoEbookSubmissionItem, PintoEbookSubmissionItemStatus } from '../../../core/graphql/types';
import EbookSubmissionItemRow from '../components/EbookSubmissionItemRow';

const PAGE_LIMIT = 10;

interface IItemStatusOption {
  label: string;
  value: PintoEbookSubmissionItemStatus | null;
}

export interface IItemStatusCount {
  status: PintoEbookSubmissionItemStatus;
  count: number;
}

export interface IPropsEbookSubmissionItemsTablePresenter {
  ebookSubmissionItems: (Pick<PintoEbookSubmissionItem, '_id' | 'status' | 'data' | 'errorNote' | 'ebookId'> & {
    ebook?: { status: EbookStatus; publishedAt?: string | null } | null;
  })[];
  currentPage: number;
  totalCount: number;
  isLoading?: boolean;
  onChangePage: (page: number) => void;
  onCancel: (itemId: string) => void;
  status: PintoEbookSubmissionItemStatus | null;
  statusOptions: IItemStatusOption[];
  onSetStatus: (status: PintoEbookSubmissionItemStatus | null) => void;
  itemStatusCounts?: IItemStatusCount[] | null;
}

function EbookSubmissionItemsTablePresenter(props: IPropsEbookSubmissionItemsTablePresenter) {
  const { ebookSubmissionItems, currentPage, totalCount, isLoading, onChangePage, onCancel, status, statusOptions, onSetStatus, itemStatusCounts } = props;

  const countByStatus = new Map((itemStatusCounts ?? []).map((item) => [item.status, item.count]));
  const allItemsCount = (itemStatusCounts ?? []).reduce((sum, item) => sum + item.count, 0);

  const countForOption = (optionStatus: PintoEbookSubmissionItemStatus | null): number => {
    return optionStatus === null ? allItemsCount : countByStatus.get(optionStatus) ?? 0;
  };

  return (
    <Card className="flex flex-col items-center space-y-16 p-8 overflow-hidden">
      <div className="w-full">
        <div className="w-full flex items-center justify-between">
          <h1 className="font-dbh text-[22px]">Items</h1>

          <div className="flex flex-wrap items-center gap-4">
            {statusOptions.map((option) => {
              const count = countForOption(option.value);
              return (
                <Button
                  round
                  type={option.value === status ? 'fill' : 'outline'}
                  key={option.value || 'all'}
                  color={option.value === status ? 'red' : 'gray'}
                  onClick={() => onSetStatus(option.value)}
                  className="h-[32px] text-12"
                >
                  {option.label?.toLocaleUpperCase()} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        <div className={classNames('mt-12 overflow-auto rounded-md transition-opacity', { 'opacity-50 pointer-events-none': isLoading && ebookSubmissionItems.length > 0 })}>
          <table className="table w-full table-zebra font-tt">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Ebook Status</th>
                <th>Ebook Published At</th>
                <th>Error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && ebookSubmissionItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-colorsBrandWarmBlack02LabelSecondary">
                    Loading items…
                  </td>
                </tr>
              ) : ebookSubmissionItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-colorsBrandWarmBlack02LabelSecondary">
                    {status ? 'No items match this status.' : 'No items in this submission yet.'}
                  </td>
                </tr>
              ) : (
                ebookSubmissionItems.map((item) => (
                  <EbookSubmissionItemRow
                    key={item._id}
                    _id={item._id}
                    status={item.status}
                    data={item.data}
                    errorNote={item.errorNote}
                    ebookId={item.ebookId}
                    ebook={item.ebook}
                    onCancel={onCancel}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-12">
          <span className="text-systemGrays02LabelSecondary">แสดงผล {PAGE_LIMIT} รายการต่อหน้า</span>

          {totalCount > 0 && <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={PAGE_LIMIT} onPageChange={onChangePage} />}
        </div>
      </div>
    </Card>
  );
}

export default EbookSubmissionItemsTablePresenter;
